import { useEffect, useState, type ComponentType, type ReactNode } from "react";

/**
 * OWNED BY C1. A code-split component (D-056), used instead of React.lazy for two reasons. After a Suspense
 * fallback shows, React holds the reveal back for up to 300 ms, so the first visit to every tab would stutter
 * even when its chunk is already downloaded. And a chunk that fails to load (offline, or a deploy that retired
 * it) throws into the nearest error boundary, which here would take the whole app down. This keeps the module
 * in a variable once loaded, so a component whose chunk was preloaded renders in the same commit as its
 * parent, and it hands the loading and failed states to the caller.
 */
export interface LazyComponent<P extends object> {
  (props: P): ReactNode;
  /** Starts the download if nothing has yet; never rejects. A failed load is retried on the next call. */
  preload(): Promise<void>;
}

export interface LazyStates {
  /** Shown until the chunk arrives. Defaults to nothing. */
  loading?: ReactNode;
  /** Shown if the chunk could not be loaded; `retry` tries again. Defaults to nothing. */
  failed?: (retry: () => void) => ReactNode;
}

/** A component's props. `Exclude` covers components declared with an optional, defaulted props argument. */
type PropsOf<C> = C extends ComponentType<infer P> ? Exclude<P, undefined> : never;

// `any` in the constraint only lets any component in; the props are then read back exactly through PropsOf.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyComponent<C extends ComponentType<any>, P extends object = PropsOf<C>>(
  load: () => Promise<C>,
  { loading = null, failed = () => null }: LazyStates = {},
): LazyComponent<P> {
  let loaded: ComponentType<P> | null = null;
  let pending: Promise<ComponentType<P>> | null = null;
  const start = (): Promise<ComponentType<P>> => {
    pending ??= load().then(
      (component) => (loaded = component),
      (error: unknown) => {
        pending = null;
        throw error;
      },
    );
    return pending;
  };

  function Lazy(props: P) {
    const [Component, setComponent] = useState<ComponentType<P> | null>(() => loaded);
    const [attempt, setAttempt] = useState(0);
    const [broken, setBroken] = useState(false);
    useEffect(() => {
      if (Component) return;
      let live = true;
      start().then(
        (component) => { if (live) setComponent(() => component); },
        () => { if (live) setBroken(true); },
      );
      return () => { live = false; };
    }, [Component, attempt]);

    if (Component) return <Component {...props} />;
    if (!broken) return loading;
    return failed(() => {
      setBroken(false);
      setAttempt((n) => n + 1);
    });
  }
  Lazy.preload = () => start().then(() => undefined, () => undefined);
  return Lazy;
}
